import React, { useState, useCallback, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import Barcode from "react-barcode";
import { Grid, Input, Button, Header, Icon, Message } from "semantic-ui-react";
import { useIsMobile } from "@utils/mobileUtils";

const COLORS = {
  primary: "#225745",
  secondary: "#f4b952",
  text: "#333",
  textSecondary: "#666",
  textTertiary: "#444",
  infoBorder: "#0066cc",
  infoBackground: "#e7f3ff",
  background: "#f8f9fa",
  white: "#fff",
  success: "#21ba45",
  warning: "#f2c037",
  error: "#db2828",
};

const FacultyLibraryIdSection = ({
  user,
  onLibraryIdUpdate,
  initialLibraryId = "",
  disabled = false,
  displayMode = "full",
  showTitle = true,
  showDescription = true,
  showInput = true,
  showButtons = true,
  showUpdateButton = true,
  showClearButton = true,
  showPrintButton = true,
}) => {
  const [libraryId, setLibraryId] = useState(
    initialLibraryId || user?.library_id || ""
  );
  const [libraryIdMessage, setLibraryIdMessage] = useState(null);
  const [isUpdatingLibraryId, setIsUpdatingLibraryId] = useState(false);
  const timeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const printRef = useRef(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (initialLibraryId !== undefined) {
      setLibraryId(initialLibraryId);
    } else if (user?.library_id !== undefined) {
      setLibraryId(user.library_id);
    }
  }, [initialLibraryId, user?.library_id]);

  const handleInputChange = useCallback(
    (value) => {
      if (isMountedRef.current) {
        setLibraryId(value);
        // Clear library ID message when typing
        if (libraryIdMessage) {
          setLibraryIdMessage(null);
        }
      }
    },
    [libraryIdMessage]
  );

  // Handle library ID update
  const handleLibraryIdUpdate = useCallback(async () => {
    if (!libraryId.trim()) {
      if (isMountedRef.current) {
        setLibraryIdMessage({
          type: "error",
          text: "Please enter a faculty library ID",
        });
      }
      return;
    }

    if (isMountedRef.current) {
      setIsUpdatingLibraryId(true);
      setLibraryIdMessage(null);
    }

    try {
      const cleanedLibraryId = libraryId.trim().toUpperCase();
      await onLibraryIdUpdate(cleanedLibraryId);

      if (isMountedRef.current) {
        setLibraryId(cleanedLibraryId);
        setLibraryIdMessage({
          type: "success",
          text: "Faculty library ID updated successfully!",
        });

        // Clear success message after 3 seconds
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            setLibraryIdMessage(null);
          }
        }, 3000);
      }
    } catch (error) {
      if (isMountedRef.current) {
        let errorMessage = "Failed to update faculty library ID";

        if (error?.response?.data?.errors) {
          const errors = error.response.data.errors;
          errorMessage = `Validation failed: ${Object.entries(errors)
            .map(([field, msg]) => `${field}: ${msg}`)
            .join(", ")}`;
        } else if (error?.message) {
          errorMessage = error.message;
        }

        setLibraryIdMessage({
          type: "error",
          text: errorMessage,
        });
      }
    } finally {
      if (isMountedRef.current) {
        setIsUpdatingLibraryId(false);
      }
    }
  }, [libraryId, onLibraryIdUpdate]);

  // Handle library ID clear
  const handleLibraryIdClear = useCallback(async () => {
    if (isMountedRef.current) {
      setIsUpdatingLibraryId(true);
      setLibraryIdMessage(null);
    }

    try {
      await onLibraryIdUpdate("");

      if (isMountedRef.current) {
        setLibraryId("");
        setLibraryIdMessage({
          type: "success",
          text: "Faculty library ID cleared successfully!",
        });

        // Clear success message after 3 seconds
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            setLibraryIdMessage(null);
          }
        }, 3000);
      }
    } catch (error) {
      if (isMountedRef.current) {
        setLibraryIdMessage({
          type: "error",
          text: error?.message || "Failed to clear faculty library ID",
        });
      }
    } finally {
      if (isMountedRef.current) {
        setIsUpdatingLibraryId(false);
      }
    }
  }, [onLibraryIdUpdate]);

  // Handle print barcode
  const handlePrintBarcode = useCallback(() => {
    if (!libraryId) return;

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      alert("Please allow popups to print the barcode");
      return;
    }

    // Generate barcode data
    const cleanId = libraryId.replace(/[^\x20-\x7E]/g, "");

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Faculty Library Barcode</title>
          <style>
            @page {
              margin: 0;
              padding: 0;
              size: A4;
            }
            body {
              margin: 0;
              padding: 20px;
              font-family: Arial, sans-serif;
              background: white;
            }
            .barcode-container {
              position: absolute;
              top: 20px;
              left: 20px;
              background: white;
              padding: 20px;
              border-radius: 8px;
              display: flex;
              flex-direction: column;
              align-items: center;
              min-width: 300px;
            }
            .faculty-id {
              font-size: 18px;
              font-weight: bold;
              color: #333;
              margin-bottom: 15px;
              letter-spacing: 1px;
            }
            .barcode-wrapper {
              border: 1px dashed #666;
              padding: 10px;
              background: white;
              border-radius: 4px;
            }
            @media print {
              body {
                margin: 0;
                padding: 20px;
              }
              .barcode-container {
                position: absolute;
                top: 20px;
                left: 20px;
              }
            }
          </style>
          <script src="https://cdnjs.cloudflare.com/ajax/libs/jsbarcode/3.11.5/JsBarcode.all.min.js"></script>
        </head>
        <body>
          <div class="barcode-container">
            <div class="faculty-id">Faculty ID: ${cleanId}</div>
            <div class="barcode-wrapper">
              <svg id="barcode"></svg>
            </div>
          </div>
          <script>
            // Generate barcode without text value
            JsBarcode("#barcode", "${cleanId}", {
              format: "CODE128",
              width: 2,
              height: 60,
              displayValue: false,
              margin: 5,
              background: "#ffffff",
              lineColor: "#000000"
            });
            
            // Print automatically after barcode is generated
            window.onload = function() {
              setTimeout(function() {
                window.focus();
                window.print();
                setTimeout(function() {
                  window.close();
                }, 500);
              }, 100);
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  }, [libraryId]);

  // Render barcode component with error handling
  const renderBarcode = useCallback((libraryIdValue) => {
    // Check if the library ID is valid for barcode generation
    if (!libraryIdValue || libraryIdValue.trim().length === 0) {
      return (
        <div
          style={{
            padding: "1rem",
            backgroundColor: "#fff3cd",
            color: "#856404",
            borderRadius: "4px",
            textAlign: "center",
          }}
        >
          <Icon name="info circle" />
          No library ID provided
        </div>
      );
    }

    try {
      const cleanId = libraryIdValue.replace(/[^\x20-\x7E]/g, "");

      if (cleanId.length === 0) {
        throw new Error("Invalid characters in library ID");
      }

      return (
        <div style={{ overflow: "hidden" }}>
          <Barcode
            value={cleanId}
            format="CODE128"
            width={2}
            height={60}
            displayValue
            fontSize={12}
            margin={10}
            background="#ffffff"
            lineColor="#000000"
            textAlign="center"
            textPosition="bottom"
          />
          {cleanId !== libraryIdValue && (
            <div
              style={{
                fontSize: "0.7rem",
                color: "#666",
                marginTop: "0.25rem",
                fontStyle: "italic",
              }}
            >
              Original ID: {libraryIdValue}
            </div>
          )}
        </div>
      );
    } catch (error) {
      return (
        <div
          style={{
            padding: "1rem",
            backgroundColor: "#ffebee",
            color: "#c62828",
            borderRadius: "4px",
            textAlign: "center",
          }}
        >
          <Icon name="warning sign" />
          <div>Unable to generate barcode</div>
          <div style={{ fontSize: "0.8rem", marginTop: "0.5rem" }}>
            Library ID: {libraryIdValue}
          </div>
        </div>
      );
    }
  }, []);

  const renderButtons = () => {
    if (!showButtons) return null;

    const buttons = [];

    if (showUpdateButton) {
      buttons.push(
        <Button
          key="update"
          primary
          size={isMobile ? "medium" : "small"}
          disabled={isUpdatingLibraryId || !libraryId.trim() || disabled}
          loading={isUpdatingLibraryId}
          onClick={handleLibraryIdUpdate}
          type="button"
          fluid={isMobile}
          content={`${
            user?.library_id || initialLibraryId ? "Update" : "Set"
          } ID`}
        />
      );
    }

    if (showClearButton && libraryId) {
      buttons.push(
        <Button
          key="clear"
          basic
          color="red"
          size={isMobile ? "medium" : "small"}
          disabled={isUpdatingLibraryId || disabled}
          onClick={handleLibraryIdClear}
          type="button"
          fluid={isMobile}
          content="Clear"
        />
      );
    }

    if (showPrintButton && libraryId) {
      buttons.push(
        <Button
          key="print"
          color="green"
          size={isMobile ? "medium" : "small"}
          content="Print"
          onClick={handlePrintBarcode}
          disabled={disabled}
          type="button"
          fluid={isMobile}
        />
      );
    }

    if (isMobile) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
            marginTop: "1rem",
          }}
        >
          {buttons}
        </div>
      );
    } else {
      return <div style={{ display: "flex", gap: "0.5rem" }}>{buttons}</div>;
    }
  };

  // Render barcode-only mode
  if (displayMode === "barcode-only") {
    return (
      <Grid.Row>
        <Grid.Column width={16}>
          <div style={{ textAlign: "center" }}>
            {/* Display barcode if library_id exists */}
            {libraryId && (
              <div
                style={{
                  padding: "1rem",
                  backgroundColor: COLORS.white,
                  borderRadius: "6px",
                  border: "1px solid #f4b952",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  {/* Render actual barcode */}
                  <div ref={printRef} style={{ marginBottom: "1rem" }}>
                    {renderBarcode(libraryId)}
                  </div>

                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: COLORS.textSecondary,
                      fontStyle: "italic",
                      marginBottom: "1rem",
                    }}
                  >
                    Use this barcode for library attendance.
                  </div>

                  {/* Show print button if enabled */}
                  {showPrintButton && (
                    <Button
                      color="green"
                      size="small"
                      icon="print"
                      content="Print"
                      onClick={handlePrintBarcode}
                      disabled={disabled}
                      type="button"
                    />
                  )}
                </div>
              </div>
            )}

            {/* Show message if no library ID */}
            {!libraryId && (
              <div
                style={{
                  padding: "2rem",
                  backgroundColor: "#fff3cd",
                  color: "#856404",
                  borderRadius: "8px",
                  textAlign: "center",
                  border: "1px solid #ffeaa7",
                }}
              >
                <Icon name="info circle" size="large" />
                <div style={{ marginTop: "1rem", fontSize: "1.1em" }}>
                  No Library ID Set
                </div>
                <p style={{ marginTop: "0.5rem", fontSize: "0.9em" }}>
                  This faculty member needs to set their library ID to generate
                  a barcode.
                </p>
              </div>
            )}
          </div>
        </Grid.Column>
      </Grid.Row>
    );
  }

  // Render full mode with conditional visibility
  return (
    <Grid.Row>
      <Grid.Column width={16}>
        <div
          style={{
            marginTop: "-.5rem",
            padding: "1rem",
            backgroundColor: COLORS.background,
            borderRadius: "8px",
            border: "1px solid #dee2e6",
          }}
        >
          {showTitle && (
            <Header
              as="h4"
              style={{ color: COLORS.primary, marginBottom: "0.5rem" }}
            >
              <Icon name="id card" />
              Faculty Library ID
            </Header>
          )}

          {showDescription && (
            <p
              style={{
                fontSize: "0.9rem",
                color: COLORS.textSecondary,
                marginBottom: "1rem",
              }}
            >
              Enter your faculty number to generate your library ID barcode.
            </p>
          )}

          {libraryIdMessage && (
            <Message
              positive={libraryIdMessage.type === "success"}
              negative={libraryIdMessage.type === "error"}
              style={{ marginBottom: "1rem" }}
            >
              {libraryIdMessage.text}
            </Message>
          )}

          {showInput && (
            <div style={{ marginBottom: isMobile ? "0" : "1rem" }}>
              {isMobile ? (
                <div>
                  <Input
                    type="text"
                    value={libraryId}
                    onChange={(e) =>
                      handleInputChange(e.target.value.toUpperCase())
                    }
                    placeholder="e.g., 0000-0000"
                    disabled={isUpdatingLibraryId || disabled}
                    fluid
                    style={{
                      textTransform: "uppercase",
                    }}
                  />
                  {renderButtons()}
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    gap: "0.5rem",
                    alignItems: "flex-end",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <Input
                      type="text"
                      value={libraryId}
                      onChange={(e) =>
                        handleInputChange(e.target.value.toUpperCase())
                      }
                      placeholder="e.g., 0000-0000"
                      disabled={isUpdatingLibraryId || disabled}
                      style={{
                        textTransform: "uppercase",
                      }}
                    />
                  </div>
                  {renderButtons()}
                </div>
              )}
            </div>
          )}

          {/* Display barcode if library_id exists */}
          {libraryId && (
            <div
              style={{
                padding: "1rem",
                backgroundColor: COLORS.white,
                borderRadius: "6px",
                border: "1px solid #f4b952",
                marginTop: "1rem",
              }}
            >
              <div style={{ textAlign: "center" }}>
                {/* Render actual barcode */}
                <div ref={printRef} style={{ marginBottom: "1rem" }}>
                  {renderBarcode(libraryId)}
                </div>

                <div
                  style={{
                    fontSize: "0.75rem",
                    color: COLORS.textSecondary,
                    fontStyle: "italic",
                  }}
                >
                  Use this barcode for library attendance.
                </div>
              </div>
            </div>
          )}
        </div>
      </Grid.Column>
    </Grid.Row>
  );
};

FacultyLibraryIdSection.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    library_id: PropTypes.string,
    department: PropTypes.string,
    program: PropTypes.string,
    year: PropTypes.string,
    semester: PropTypes.string,
    roles: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.array,
    ]),
  }),
  onLibraryIdUpdate: PropTypes.func.isRequired,
  initialLibraryId: PropTypes.string,
  disabled: PropTypes.bool,
  displayMode: PropTypes.oneOf(["full", "barcode-only"]),
  showTitle: PropTypes.bool,
  showDescription: PropTypes.bool,
  showInput: PropTypes.bool,
  showButtons: PropTypes.bool,
  showUpdateButton: PropTypes.bool,
  showClearButton: PropTypes.bool,
  showPrintButton: PropTypes.bool,
};

FacultyLibraryIdSection.defaultProps = {
  user: {},
  initialLibraryId: "",
  disabled: false,
  displayMode: "full",
  showTitle: true,
  showDescription: true,
  showInput: true,
  showButtons: true,
  showUpdateButton: true,
  showClearButton: true,
  showPrintButton: true,
};

export default FacultyLibraryIdSection;
